import { useCallback } from 'react'
import SharedFilters from '../../../components/SharedFilters'
import AppPagination from '../../../components/AppPagination'
import { useExportJob } from '../../../hooks/useExportJob'
import { useInstallmentsListScreen } from './hooks/useInstallmentsListScreen'
import InstallmentsSettingsModal from './components/InstallmentsSettingsModal'

const SEARCH_FIELD_OPTIONS = [
  { value: 'Name', label: 'Name' },
  { value: 'Roll No.', label: 'Roll No.' },
  { value: 'ID No.', label: 'ID No.' },
  { value: 'Phone No.', label: 'Phone No.' },
]

function formatCurrency(value) {
  return `₹${Number(value || 0).toLocaleString('en-IN')}`
}

function formatDate(value) {
  if (!value) return ''
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

export default function InstallmentsListScreen() {
  const { state, actions } = useInstallmentsListScreen()

  const toOptions = useCallback((arr) => {
    return (arr || []).map((item) =>
      typeof item === 'object' && item !== null
        ? item
        : { value: item, label: item }
    )
  }, [])

  const { triggerExport, isExporting } = useExportJob({ module: 'installments' })

  const handleExportCSV = () => {
    triggerExport({
      searchField: state.searchField,
      searchText: state.searchText,
      status: state.tab === 'all' ? '' : state.tab === 'archive' ? 'Inactive' : 'Active',
      company: state.company === 'Select' ? '' : state.company,
      branch: state.branch === 'Select' ? '' : state.branch,
      course: state.course === 'Select' ? '' : state.course,
      batch: state.batch === 'Select' ? '' : state.batch,
      scheme: state.scheme === 'Select' ? '' : state.scheme,
      paymentStatus: state.paymentStatus === 'All' ? '' : state.paymentStatus,
      fromDate: state.fromDate,
      toDate: state.toDate,
    })
  }

  const fromEntry = state.totalCount === 0 ? 0 : (state.currentPage - 1) * state.pageSize + 1
  const toEntry = Math.min(state.currentPage * state.pageSize, state.totalCount)

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* ── Page Header (Shared ERP Theme) ─────────────────────────────────── */}
      <section className="flex flex-col gap-5 border-b border-outline-variant/10 pb-5 xl:flex-row xl:items-end xl:justify-between">
        <div className="max-w-4xl">
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Admission Module</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Installments
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Search, filter and manage student fee installments, due dates, and balances.
          </p>
        </div>

        {/* ── Action Cluster: Settings + Status Tabs ───────────────────────── */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={actions.openSettings}
            className="inline-flex items-center gap-2 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2 text-xs font-bold text-on-surface shadow-xs hover:bg-surface-container-high transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-base text-primary">settings</span>
            <span>Alert Settings</span>
          </button>

          <div className="flex items-center gap-1.5 rounded-xl bg-surface-container-high p-1">
            {['Active', 'Archive', 'All'].map((tabLabel) => {
              const tabKey = tabLabel.toLowerCase()
              const isActive = state.tab === tabKey
              return (
                <button
                  key={tabKey}
                  type="button"
                  onClick={() => actions.handleTabChange(tabKey)}
                  className={`rounded-lg px-4 py-2 text-xs font-bold transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-surface-container-lowest text-primary shadow-xs'
                      : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  {tabLabel}
                </button>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── 4 Interactive KPI Summary Cards (Click to Filter) ──────────────── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {/* Total Fees */}
        <button
          type="button"
          onClick={() => actions.handleKpiCardClick('All')}
          className={`flex items-center gap-3.5 rounded-2xl border bg-surface-container-lowest p-4 text-left shadow-xs transition hover:shadow-md cursor-pointer ${
            state.kpiFilter === 'All'
              ? 'border-blue-500 ring-2 ring-blue-500/20 bg-blue-50/20'
              : 'border-outline-variant/25'
          }`}
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <span className="material-symbols-outlined text-xl">account_balance_wallet</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Total Fees</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-on-surface tabular-nums">
              {formatCurrency(state.summary.total)}
            </p>
          </div>
        </button>

        {/* Paid (Receipts) */}
        <button
          type="button"
          onClick={() => actions.handleKpiCardClick('Paid')}
          className={`flex items-center gap-3.5 rounded-2xl border bg-surface-container-lowest p-4 text-left shadow-xs transition hover:shadow-md cursor-pointer ${
            state.kpiFilter === 'Paid'
              ? 'border-emerald-500 ring-2 ring-emerald-500/20 bg-emerald-50/20'
              : 'border-outline-variant/25'
          }`}
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <span className="material-symbols-outlined text-xl">check_circle</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Paid (Receipts)</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-emerald-700 tabular-nums">
              {formatCurrency(state.summary.paid)}
            </p>
          </div>
        </button>

        {/* Balance */}
        <button
          type="button"
          onClick={() => actions.handleKpiCardClick('Balance')}
          className={`flex items-center gap-3.5 rounded-2xl border bg-surface-container-lowest p-4 text-left shadow-xs transition hover:shadow-md cursor-pointer ${
            state.kpiFilter === 'Balance'
              ? 'border-purple-500 ring-2 ring-purple-500/20 bg-purple-50/20'
              : 'border-outline-variant/25'
          }`}
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
            <span className="material-symbols-outlined text-xl">pending</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Balance</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-purple-700 tabular-nums">
              {formatCurrency(state.summary.balance)}
            </p>
          </div>
        </button>

        {/* Due / Overdue */}
        <button
          type="button"
          onClick={() => actions.handleKpiCardClick('Overdue')}
          className={`flex items-center gap-3.5 rounded-2xl border bg-surface-container-lowest p-4 text-left shadow-xs transition hover:shadow-md cursor-pointer ${
            state.kpiFilter === 'Overdue'
              ? 'border-amber-500 ring-2 ring-amber-500/20 bg-amber-50/20'
              : 'border-outline-variant/25'
          }`}
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
            <span className="material-symbols-outlined text-xl">warning</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Due / Overdue</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-amber-700 tabular-nums">
              {formatCurrency(state.summary.overdue)}
            </p>
          </div>
        </button>
      </div>

      {/* ── SharedFilters Component (Unified Project Pattern with Tools) ─────── */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        searchFieldOptions={SEARCH_FIELD_OPTIONS}
        showStatus={false}
        showSearchButtons={true}
        companyFilter={state.company}
        setCompanyFilter={actions.setCompany}
        companyOptions={toOptions(state.options.companyOptions)}
        branchFilter={state.branch}
        setBranchFilter={actions.setBranch}
        branchOptions={toOptions(state.options.branchOptions)}
        courseFilter={state.course}
        setCourseFilter={actions.setCourse}
        courseOptions={toOptions(state.options.courseOptions)}
        schemeFilter={state.scheme}
        setSchemeFilter={actions.setScheme}
        schemeOptions={toOptions(state.options.schemeOptions)}
        statusFilter={state.status}
        setStatusFilter={actions.setStatus}
        statusOptions={toOptions(state.options.statusOptions)}
        academicYearFilter={state.academicYear}
        setAcademicYearFilter={actions.setAcademicYear}
        academicYearOptions={toOptions(state.options.academicYearOptions)}
        periodFilter={state.datePeriod}
        setPeriodFilter={actions.handleDatePeriodChange}
        periodOptions={state.options.datePeriodOptions}
        periodLabel="Date: Period"
        fromDate={state.fromDate}
        setFromDate={actions.setFromDate}
        toDate={state.toDate}
        setToDate={actions.setToDate}
        batchFilter={state.batch}
        setBatchFilter={actions.setBatch}
        batchOptions={toOptions(state.options.batchOptions)}
        subjectFilter={state.subject}
        setSubjectFilter={actions.setSubject}
        subjectOptions={toOptions(state.options.subjectOptions)}
        genderFilter={state.gender}
        setGenderFilter={actions.setGender}
        genderOptions={toOptions(state.options.genderOptions)}
        onSearch={actions.search}
        onReset={actions.resetFilters}
        resetLabel="Show All"
        visibleAccordionFilters={[
          'company',
          'academicYear',
          'branch',
          'period',
          'fromDate',
          'toDate',
          'course',
          'batch',
          'scheme',
          'subject',
          'gender',
        ]}
        tools={[
          { key: 'export', label: isExporting ? 'Exporting...' : 'Export (CSV)', icon: 'download' },
          { key: 'print', label: 'Print List', icon: 'print' },
        ]}
        onToolSelect={(key) => {
          if (key === 'export') handleExportCSV()
          else if (key === 'print') window.print()
        }}
      />

      {/* ── Data Table Section (5 Columns Matching Legacy ASPX Parity) ─────── */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs">
        {/* Table Title Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-outline-variant/15 bg-surface-container-low/40 px-6 py-3">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-base text-primary">payments</span>
            <span className="text-xs font-bold uppercase tracking-wider text-on-surface">
              List of Installments
            </span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-on-surface-variant">
              {state.totalCount} {state.totalCount === 1 ? 'Record' : 'Records'}
            </span>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant select-none">
                <th className="w-12 px-5 py-3.5 text-center">
                  <input
                    type="checkbox"
                    checked={state.rows.length > 0 && state.selectedRowIds.size === state.rows.length}
                    onChange={(e) => actions.toggleAllRows(e.target.checked)}
                    className="h-4 w-4 cursor-pointer rounded border-outline-variant/40 text-primary focus:ring-primary/20"
                    aria-label="Select all installments"
                  />
                </th>
                <th className="px-5 py-3.5 font-semibold text-on-surface-variant">Name/Mobile/Email</th>
                <th className="px-5 py-3.5 font-semibold text-on-surface-variant">Company-Branch/Course/Batch</th>
                <th className="px-5 py-3.5 font-semibold text-on-surface-variant">Amount/Particulars</th>
                <th className="px-5 py-3.5 font-semibold text-on-surface-variant">Balance</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-outline-variant/15 text-xs">
              {state.isLoading ? (
                <tr>
                  <td colSpan={5} className="py-14 text-center text-sm font-medium text-on-surface-variant">
                    <span className="material-symbols-outlined animate-spin text-3xl text-primary/40 block mb-2">
                      progress_activity
                    </span>
                    Loading installments...
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-14 text-center">
                    <span className="material-symbols-outlined mb-2 block text-4xl text-on-surface-variant/40">
                      payments
                    </span>
                    <p className="text-sm font-medium text-on-surface-variant">
                      No installments match the current filters.
                    </p>
                    <button
                      type="button"
                      onClick={actions.resetFilters}
                      className="mt-3 inline-flex items-center gap-1 rounded-xl bg-primary/10 px-4 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition cursor-pointer"
                    >
                      Show All
                    </button>
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => (
                  <tr
                    key={row.id}
                    className="transition-colors hover:bg-surface-container-low/40"
                  >
                    {/* Col 1: Checkbox */}
                    <td className="w-12 px-5 py-3.5 text-center align-top pt-4">
                      <input
                        type="checkbox"
                        checked={state.selectedRowIds.has(row.id)}
                        onChange={() => actions.toggleRow(row.id)}
                        className="h-4 w-4 cursor-pointer rounded border-outline-variant/40 text-primary focus:ring-primary/20"
                        aria-label={`Select installment for ${row.student}`}
                      />
                    </td>

                    {/* Col 2: Name/Mobile/Email */}
                    <td className="px-5 py-3.5 align-top">
                      <div className="flex flex-col gap-0.5">
                        {row.dueDate ? (
                          <span className="text-[11px] font-medium text-on-surface-variant/80">
                            (Due Date: {formatDate(row.dueDate)})
                          </span>
                        ) : null}

                        <div>
                          <button
                            type="button"
                            onClick={() => actions.handleOpenFeeLedger(row.studentId)}
                            className="font-bold text-primary hover:underline text-left cursor-pointer"
                          >
                            {row.student} {row.rollNo ? `(Roll No. ${row.rollNo})` : ''}
                          </button>
                        </div>

                        {row.mobile && (
                          <span className="text-[11px] font-medium text-on-surface-variant">
                            {row.mobile}
                          </span>
                        )}
                        {row.email && (
                          <span className="text-[11px] text-on-surface-variant/70">
                            {row.email}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Col 3: Company-Branch/Course/Batch */}
                    <td className="px-5 py-3.5 align-top">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-semibold text-on-surface">
                          {row.company || '—'} {row.branch ? `(${row.branch})` : ''}
                        </span>
                        <span className="text-on-surface-variant font-medium leading-relaxed">
                          {row.course || '—'}
                        </span>
                        <span className="text-on-surface-variant/70 text-[11px]">
                          {row.batch || '—'}
                        </span>
                      </div>
                    </td>

                    {/* Col 4: Amount/Particulars */}
                    <td className="px-5 py-3.5 align-top">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-bold text-on-surface text-sm tabular-nums">
                          ₹{Number(row.amount || 0).toLocaleString('en-IN')}
                        </span>
                        <span className="text-on-surface-variant font-medium">
                          {row.particulars || '—'}
                        </span>
                      </div>
                    </td>

                    {/* Col 5: Balance */}
                    <td className="px-5 py-3.5 align-top">
                      <div className="flex flex-col gap-1">
                        <span className="font-bold text-on-surface text-sm tabular-nums">
                          ₹{Number(row.balance || 0).toLocaleString('en-IN')}
                        </span>
                        <span
                          className={`inline-flex w-fit items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold ${
                            row.paymentStatus === 'Paid'
                              ? 'bg-emerald-50 text-emerald-700'
                              : row.paymentStatus === 'Overdue'
                              ? 'bg-amber-50 text-amber-700'
                              : 'bg-purple-50 text-purple-700'
                          }`}
                        >
                          {row.paymentStatus}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination Footer ───────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 border-t border-outline-variant/15 bg-surface-container-low/30 px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs font-semibold text-on-surface-variant">
            Showing {fromEntry}–{toEntry} of {state.totalCount} entries
          </span>

          {state.totalPages > 1 && (
            <AppPagination
              page={state.currentPage}
              totalPages={state.totalPages}
              onPageChange={actions.setCurrentPage}
              onPrevious={() => actions.setCurrentPage(Math.max(1, state.currentPage - 1))}
              onNext={() => actions.setCurrentPage(Math.min(state.totalPages, state.currentPage + 1))}
              canPrevious={state.currentPage > 1}
              canNext={state.currentPage < state.totalPages}
            />
          )}
        </div>
      </section>

      {/* ── Installment Alerts Configuration Modal ──────────────────────── */}
      <InstallmentsSettingsModal
        isOpen={state.isSettingsOpen}
        onClose={actions.closeSettings}
      />
    </div>
  )
}